import { DEPLOYMENTS_RECENT_PER_APP } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AppList, CommitResults } from "../../components/deployments/app-list";
import { DeploymentHistoryDialog } from "../../components/deployments/history-dialog";
import { useNow } from "../../components/deployments/status-chip";
import { Icon } from "../../components/shell/icons";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { isTransientError } from "../../lib/api/messages";
import { formatClockExact, formatDateTime } from "../../lib/i18n/format";
import { overviewQuery } from "../../lib/deployments/queries";
import { parseCommitQuery } from "../../lib/deployments/status";

/**
 * Deployments: "is my push live, still building, or did it fail — and why?"
 * read from Dokploy through the API, so nobody has to ask the one person who
 * can open Dokploy. The commit search answers the first question; an app's
 * history and build log the second. Read-only.
 *
 * The overview polls while the tab is visible; when Dokploy does not answer,
 * the last answer stays on screen with a quiet note. `?app=` / `?d=` open an
 * app's history at a deployment (shareable), `?q=` keeps the search.
 */
const search = z.object({
	app: z.string().min(1).max(128).optional().catch(undefined),
	d: z.string().min(1).max(128).optional().catch(undefined),
	q: z.string().max(200).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/deployments")({
	validateSearch: search,
	component: Deployments,
});

function Deployments() {
	const { t } = useTranslation("pages");
	const params = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const q = useQuery(overviewQuery());
	const data = q.data;

	// The box is typed into freely; the URL (and the answer) follows a beat later.
	const [text, setText] = useState(params.q ?? "");
	useEffect(() => {
		const id = window.setTimeout(() => {
			const next = text.trim() || undefined;
			if (next !== params.q) void navigate({ search: (prev) => ({ ...prev, q: next }), replace: true });
		}, 250);
		return () => window.clearTimeout(id);
	}, [text, params.q, navigate]);
	const query = useMemo(() => parseCommitQuery(params.q ?? ""), [params.q]);

	const apps = data?.apps ?? [];
	const now = useNow(apps.some((a) => a.recent[0]?.status === "running"));
	const dokployDown = q.failureReason != null && isTransientError(q.failureReason);

	const openApp = (appId: string, deploymentId?: string) => void navigate({ search: (prev) => ({ ...prev, app: appId, d: deploymentId }) });
	const pick = (deploymentId: string) => void navigate({ search: (prev) => ({ ...prev, d: deploymentId }), replace: true });
	const close = () => void navigate({ search: (prev) => ({ ...prev, app: undefined, d: undefined }) });

	return (
		<div className="mx-auto w-full max-w-[60rem] px-[1.5rem] pt-[3rem] pb-[4rem] sm:px-[3rem]">
			<h1 className="flex items-center gap-[0.625rem] font-black text-[2rem] text-ink">
				<Icon name="rocket" className="size-[1.75rem] text-muted" />
				{t("deployments.title")}
			</h1>
			<p className="mt-[0.25rem] mb-[1.25rem] max-w-[60ch] text-muted">{t("deployments.subtitle")}</p>

			{q.isPending ? (
				<Spinner className="size-[1.25rem] text-muted" />
			) : !data ? (
				dokployDown ? (
					<p role="status" className="text-[0.875rem] text-wait">
						{t("deployments.unreachable")}
					</p>
				) : (
					<button type="button" onClick={() => void q.refetch()} className="cursor-pointer text-conflict underline">
						{t("deployments.loadError")}
					</button>
				)
			) : !data.configured ? (
				<EmptyState icon={<Icon name="rocket" className="size-[1.25rem]" />} title={t("deployments.notConnected.title")} body={t("deployments.notConnected.body")} />
			) : (
				<>
					<div className="mb-[1.25rem] rounded-card border border-rule bg-paper px-[1rem] py-[0.875rem]">
						<label htmlFor="wn-find-commit" className="mb-[0.375rem] block font-bold text-[0.9375rem] text-ink">
							{t("deployments.find.label")}
						</label>
						<div className="relative max-w-[36rem]">
							<Icon name="search" className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-[0.625rem] size-[1rem] text-muted" />
							<input
								id="wn-find-commit"
								type="search"
								value={text}
								onChange={(e) => setText(e.target.value)}
								placeholder={t("deployments.find.placeholder")}
								spellCheck={false}
								autoComplete="off"
								className="min-h-[2.5rem] w-full rounded-ctl border border-rule bg-sheet pr-[0.75rem] pl-[2.125rem] text-[0.9375rem] focus:border-board"
							/>
						</div>
						<p className="mt-[0.375rem] text-[0.75rem] text-muted">{t("deployments.find.hint", { count: DEPLOYMENTS_RECENT_PER_APP })}</p>
					</div>

					<div className="mb-[0.75rem] flex flex-wrap items-center gap-x-[0.75rem] gap-y-[0.25rem] text-[0.8125rem] text-muted">
						{data.fetched_at && (
							<time dateTime={data.fetched_at} title={formatDateTime(data.fetched_at)} className="tabular-nums">
								{t("deployments.updated", { time: formatClockExact(data.fetched_at) })}
							</time>
						)}
						{dokployDown && (
							<span role="status" className="text-wait">
								{t("deployments.unreachable")}
							</span>
						)}
					</div>

					{apps.length === 0 ? (
						<p className="text-muted">{t("deployments.noApps")}</p>
					) : query ? (
						<CommitResults apps={apps} query={query} now={now} onOpen={openApp} />
					) : (
						<AppList apps={apps} now={now} onOpen={(id) => openApp(id)} />
					)}
				</>
			)}

			{params.app && (
				<DeploymentHistoryDialog
					key={params.app}
					appId={params.app}
					deploymentId={params.d}
					fallback={apps.find((a) => a.app_id === params.app)}
					onPick={pick}
					onClose={close}
				/>
			)}
		</div>
	);
}
